// src/contexts/ProfileContext.jsx

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from "react";
// Import all the necessary API functions for the refresh
import {
  getUserProfile,
  getAllExperience,
  getAllEducation,
  getUserSkills,
  getUserDocuments,
} from "../services/api";
// Import our updated master save function
import {
  saveFullProfile,
  processResumeUploadData,
} from "../services/profileSubmitter";

const ProfileContext = createContext();

export const STEPS = {
  ACCOUNT: 0,
  EDUCATION: 1,
  EXPERIENCE: 2,
  SKILLS: 3,
  PREFERENCES: 4,
  DOCUMENTS: 5,
  SKILL_TEST: 6,
};

const initialState = {
  formData: {
    userId: null,
    account: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      accountType: "Job Seeker",
      timeZone: "Eastern",
      photo: null,
      photoUrl: null,
      profession: "",
      headline: "",
      bio: "",
      location: "",
    },
    educations: [],
    experiences: [],
    skills: { skills: [], licenses: [] },
    preferences: {
      desiredJobTitles: "",
      preferredIndustries: "",
      minSalary: "",
      workArrangement: "Remote",
      companySize: "Startup (1-10)",
      values: "",
      dealBreakers: [],
    },
    documents: { resume: null, coverLetter: null, portfolio: null },
    skillTest: {},
    _internal: { hasAnyExistingDocument: false },
  },
  currentStep: STEPS.ACCOUNT,
  isSubmitting: false,
  error: null,
  isProfileComplete: false,
};

const ActionTypes = {
  INITIALIZE_FORM: "INITIALIZE_FORM",
  UPDATE_FORM_SECTION: "UPDATE_FORM_SECTION",
  SET_STEP: "SET_STEP",
  SET_SUBMITTING: "SET_SUBMITTING",
  SET_ERROR: "SET_ERROR",
  RESET_FORM: "RESET_FORM",
  CLEAR_ALL_DATA: "CLEAR_ALL_DATA",
};

const profileReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.INITIALIZE_FORM:
      const initializedAccount = {
        ...initialState.formData.account,
        ...(action.payload.account || {}),
      };
      return {
        ...initialState,
        formData: {
          ...initialState.formData,
          ...action.payload,
          account: initializedAccount,
          skills: {
            ...initialState.formData.skills,
            ...(action.payload.skills || {}),
          },
          preferences: {
            ...initialState.formData.preferences,
            ...(action.payload.preferences || {}),
          },
          documents: {
            resume: action.payload.documents?.resume || null,
            coverLetter: action.payload.documents?.coverLetter || null,
            portfolio: action.payload.documents?.portfolio || null,
          },
          skillTest: {
            ...initialState.formData.skillTest,
            ...(action.payload.skillTest || {}),
          },
          _internal: {
            ...initialState.formData._internal,
            ...(action.payload._internal || {}),
          },
        },
        error: null,
        isProfileComplete: action.payload.isProfileComplete || false,
        currentStep: state.currentStep,
      };
    case ActionTypes.UPDATE_FORM_SECTION: {
      const { section, data } = action.payload;
      if (section && state.formData[section] !== undefined) {
        let updatedSectionData;
        if (
          typeof state.formData[section] === "object" &&
          state.formData[section] !== null &&
          !Array.isArray(state.formData[section])
        ) {
          updatedSectionData = { ...state.formData[section], ...data };
        } else {
          updatedSectionData = data;
        }
        return {
          ...state,
          formData: { ...state.formData, [section]: updatedSectionData },
          error: null,
        };
      }
      return state;
    }
    case ActionTypes.SET_STEP:
      return { ...state, currentStep: action.payload, error: null };
    case ActionTypes.SET_SUBMITTING:
      return { ...state, isSubmitting: action.payload };
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, isSubmitting: false };
    case ActionTypes.RESET_FORM:
      return initialState;
    case ActionTypes.CLEAR_ALL_DATA:
      return {
        ...initialState,
        formData: {
          ...initialState.formData,
          userId: state.formData.userId,
          account: {
            ...initialState.formData.account,
            email: action.payload?.keepEmail || "",
          },
        },
        currentStep: STEPS.ACCOUNT,
        error: null,
      };
    default:
      return state;
  }
};

export const ProfileProvider = ({ children }) => {
  const [state, dispatch] = useReducer(profileReducer, initialState);

  const initializeForm = useCallback((fetchedData) => {
    dispatch({ type: ActionTypes.INITIALIZE_FORM, payload: fetchedData });
  }, []);

  const updateFormSection = useCallback(({ section, data }) => {
    dispatch({
      type: ActionTypes.UPDATE_FORM_SECTION,
      payload: { section, data },
    });
  }, []);

  const setStep = useCallback((step) => {
    dispatch({ type: ActionTypes.SET_STEP, payload: step });
  }, []);

  const setSubmitting = useCallback((isSubmitting) => {
    dispatch({ type: ActionTypes.SET_SUBMITTING, payload: isSubmitting });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const resetForm = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_FORM });
  }, []);

  const goToNextStep = useCallback(() => {
    setStep(state.currentStep + 1);
  }, [state.currentStep, setStep]);

  const goToPreviousStep = useCallback(() => {
    setStep(Math.max(0, state.currentStep - 1));
  }, [state.currentStep, setStep]);

  const clearAllData = useCallback(
    (options = {}) => {
      dispatch({
        type: ActionTypes.CLEAR_ALL_DATA,
        payload: {
          keepEmail: options.keepEmail ? state.formData.account?.email : "",
        },
      });
    },
    [state.formData.account?.email]
  );

  // --- UPDATED DATA LOADING FUNCTION ---
  const refreshProfileData = useCallback(async () => {
    console.log("[ProfileContext] Refreshing all profile data from server...");
    dispatch({ type: ActionTypes.SET_SUBMITTING, payload: true });
    try {
      const [
        profileRes,
        experiencesRes,
        educationsRes,
        skillsRes,
        documentsRes,
      ] = await Promise.all([
        getUserProfile(),
        getAllExperience(),
        getAllEducation(),
        getUserSkills(),
        getUserDocuments(),
      ]);

      // Debug education data
      console.log(
        "[ProfileContext] Education data from server:",
        educationsRes
      );
      educationsRes?.forEach((edu, idx) => {
        console.log(
          `Education ${idx} description:`,
          edu.description || "NO DESCRIPTION"
        );
      });

      const transformedData = {
        account: {
          ...state.formData.account,
          firstName: profileRes.full_name?.split(" ")[0] || "",
          lastName: profileRes.full_name?.split(" ").slice(1).join(" ") || "",
          email: profileRes.email || state.formData.account.email,
          phoneNumber: profileRes.phone_number,
          photoUrl: profileRes.profile_picture,
          profession: profileRes.profession,
          headline: profileRes.headline,
          bio: profileRes.bio,
          location: profileRes.location,
        },
        experiences: experiencesRes || [],
        educations: educationsRes || [],
        skills: {
          skills: skillsRes?.map((s) => s.skill.name) || [],
          licenses: profileRes.licenses || [],
        },
        _internal: {
          hasAnyExistingDocument: documentsRes && documentsRes.length > 0,
        },
        isProfileComplete: profileRes.is_complete || false,
      };

      dispatch({ type: ActionTypes.INITIALIZE_FORM, payload: transformedData });
      console.log(
        "[ProfileContext] Profile data successfully refreshed and initialized."
      );
    } catch (err) {
      console.error("[ProfileContext] Failed to refresh profile data:", err);
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: err.message || "Failed to load latest profile data.",
      });
    } finally {
      dispatch({ type: ActionTypes.SET_SUBMITTING, payload: false });
    }
  }, [state.formData.account]);

  // --- UPDATED MASTER SAVE FUNCTION ---
  const submitFullProfile = useCallback(
    async (parsedResumeData = null) => {
      console.log("[ProfileContext] Submitting full profile...");
      console.log(
        "[ProfileContext] With parsed resume data:",
        parsedResumeData
      );
      dispatch({ type: ActionTypes.SET_SUBMITTING, payload: true });
      dispatch({ type: ActionTypes.SET_ERROR, payload: null });
      try {
        // Use the updated helper function that handles education descriptions
        await saveFullProfile(state.formData, parsedResumeData);
        console.log("[ProfileContext] Full profile submitted successfully.");
        // Refresh data to ensure state is in sync
        await refreshProfileData();
        return true;
      } catch (error) {
        console.error("[ProfileContext] Error submitting full profile:", error);
        dispatch({
          type: ActionTypes.SET_ERROR,
          payload:
            error.message || "An unexpected error occurred while saving.",
        });
        return false;
      } finally {
        dispatch({ type: ActionTypes.SET_SUBMITTING, payload: false });
      }
    },
    [state.formData, refreshProfileData]
  );

  // --- NEW FUNCTION: Process Resume Upload ---
  const processResumeUpload = useCallback(
    async (parsedResumeData) => {
      console.log("[ProfileContext] Processing resume upload data");
      dispatch({ type: ActionTypes.SET_SUBMITTING, payload: true });
      dispatch({ type: ActionTypes.SET_ERROR, payload: null });

      try {
        // Use the new processResumeUploadData function
        const saveResults = await processResumeUploadData(parsedResumeData);

        // Update local form state with parsed data
        if (parsedResumeData.skills_and_profile) {
          const nameParts = (
            parsedResumeData.skills_and_profile.full_name || ""
          ).split(" ");
          updateFormSection({
            section: "account",
            data: {
              firstName: nameParts[0] || "",
              lastName: nameParts.slice(1).join(" ") || "",
              phoneNumber:
                parsedResumeData.skills_and_profile.phone_number || "",
              location: parsedResumeData.skills_and_profile.location || "",
              headline: parsedResumeData.skills_and_profile.headline || "",
            },
          });

          updateFormSection({
            section: "skills",
            data: {
              skills: parsedResumeData.skills_and_profile.skills || [],
              licenses: parsedResumeData.skills_and_profile.licenses || [],
            },
          });
        }

        // Refresh to get all saved data including education with descriptions
        await refreshProfileData();

        console.log(
          "[ProfileContext] Resume processing complete:",
          saveResults
        );
        return saveResults;
      } catch (error) {
        console.error(
          "[ProfileContext] Error processing resume upload:",
          error
        );
        dispatch({
          type: ActionTypes.SET_ERROR,
          payload: error.message || "Failed to process resume data.",
        });
        throw error;
      } finally {
        dispatch({ type: ActionTypes.SET_SUBMITTING, payload: false });
      }
    },
    [refreshProfileData, updateFormSection]
  );

  const value = useMemo(
    () => ({
      formData: state.formData,
      currentStep: state.currentStep,
      isSubmitting: state.isSubmitting,
      error: state.error,
      isProfileComplete: state.isProfileComplete,
      initializeForm,
      updateFormSection,
      setStep,
      setSubmitting,
      setError,
      resetForm,
      goToNextStep,
      goToPreviousStep,
      refreshProfileData,
      submitFullProfile,
      processResumeUpload, // New function
      clearAllData,
      STEPS,
    }),
    [
      state,
      initializeForm,
      updateFormSection,
      setStep,
      setSubmitting,
      setError,
      resetForm,
      goToNextStep,
      goToPreviousStep,
      refreshProfileData,
      submitFullProfile,
      processResumeUpload,
      clearAllData,
    ]
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};
